import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import type { ReturnsPosition } from '@lensmira/domain'

import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'
import { Ltr, useTranslation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { DataSurface } from './DataSurface.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { returnsPositionLabel } from '../status-labels.js'
import type { QueuedWrite } from '../queue/types.js'

/**
 * Returns and exchanges. §6.7 `POS-006`; §6.39 `SAL-011`, `SAL-012`.
 *
 * ---
 *
 * **The notice comes before the attempt, and that is the whole shape of this screen.**
 *
 * `SAL-011` `MUST`: "the listing or receipt says so before purchase, not after." The counter's half
 * of that is this screen: `GET /orders/returns/:orderId` is read directly, the way `CustomerSearch`
 * reads (`OFF-001a` is about writes, and this is not one), and its `notice` string is shown verbatim
 * next to the line it describes before the return form is reachable at all. It is not composed here
 * because the server is deliberately careful not to state a return window the shop has never
 * configured (`returnsNotice` in `packages/domain`), and a sentence written on this screen instead
 * could say something the server was careful not to.
 *
 * A failed read is refused rather than degraded. `CustomerSearch` falls back to an empty list on a
 * failed search because a dispenser can still type a prescription without one; here, an empty
 * fallback would let a return happen with no notice ever shown, which is exactly what `SAL-011`
 * forbids. So a failed read shows the failure and a retry, and offers no line to return.
 *
 * **The return itself is a queued write**, exactly as `RxCapture` queues a prescription: `enqueue`,
 * never `fetch`, and the write's outcome is read back from the queue snapshot rather than assumed.
 *
 * **The goodwill override is a second, deliberate write, not a flag on the first one.**
 * `resolveReturn` in `packages/domain` sends four refusal codes down a path that needs
 * `goodwill_override` rather than refusing outright: `RETURN_MADE_TO_SPECIFICATION`,
 * `RETURN_WINDOW_UNSET`, `RETURN_WINDOW_EXPIRED`, `RETURN_POLICY_UNSET`. The task is explicit that
 * this must never be a checkbox left ticked, so it is not a form field at all: it only appears after
 * the ordinary return has already been refused for one of those four reasons, it names the refusal
 * verbatim and asks for a second confirmation naming the consequence, and only then discards the
 * blocked write and queues a new one carrying `goodwillOverride: true`. `RETURN_EXCEEDS_SOLD` gets
 * none of this: `POS-006`'s override path is for policy, not for arithmetic, and the task calls it
 * "a flat refusal with no override".
 *
 * **What this screen cannot show.** `apps/counter/src/queue/transport.ts` reports only
 * `{ kind: 'accepted' }` on success, with no response body, which is the same reason `RxCapture`
 * never shows what it saved. The API's `ReturnedLine` carries `refund` and `tax` as `MoneyJson`, and
 * neither reaches here: there is no money value on this screen to format, so no money helper is
 * written for one. Flagged in the handoff rather than left silent.
 */

/** `SAL-011`'s per-line notice, read before any return is offered. Mirrors the API's response shape. */
interface LineReturnsPolicy {
  readonly orderLineId: string
  readonly returnsPosition: ReturnsPosition
  readonly notice: string
  readonly quantitySold: number
  readonly quantityReturned: number
}

interface ReturnsPolicyResponse {
  readonly lines?: readonly LineReturnsPolicy[]
}

/** `resolveReturn`'s `needs_override` codes: refused, but not a wall. `POS-006`. */
const OVERRIDE_ELIGIBLE_CODES = new Set([
  'RETURN_MADE_TO_SPECIFICATION',
  'RETURN_WINDOW_UNSET',
  'RETURN_WINDOW_EXPIRED',
  'RETURN_POLICY_UNSET',
])

/**
 * Why the read failed, kept as data and not as a sentence. The sentence is built at render, so a
 * change of language does not need the effect below to run again, which would refetch the policy.
 */
type LoadError =
  | { readonly kind: 'refused'; readonly status: number }
  | { readonly kind: 'other'; readonly message: string | null }

class PolicyRefused extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${String(status)}`)
  }
}

export interface ReturnLineProps {
  readonly orderId: string
  /** `BR-08`: a return is a sale running backwards and records the same attribution. */
  readonly branchId: string
  readonly terminalId: string
  /** Required by the server only when cash actually goes back over the counter; resolved by caller. */
  readonly cashSessionId?: string
  readonly baseUrl: string
  readonly token: string
  readonly doFetch: typeof globalThis.fetch
  readonly onDone?: () => void
}

function writeIsGoodwillOverride(write: QueuedWrite): boolean {
  return (write.body as { goodwillOverride?: unknown } | null)?.goodwillOverride === true
}

export function ReturnLine({
  orderId,
  branchId,
  terminalId,
  cashSessionId,
  baseUrl,
  token,
  doFetch,
  onDone,
}: ReturnLineProps): JSX.Element {
  const [lines, setLines] = useState<readonly LineReturnsPolicy[] | null>(null)
  const [loadError, setLoadError] = useState<LoadError | null>(null)
  const { t } = useTranslation()
  const [reloadToken, setReloadToken] = useState(0)

  const [selectedLineId, setSelectedLineId] = useState<string | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [reason, setReason] = useState('')
  const [restocked, setRestocked] = useState(false)
  const [writeId, setWriteId] = useState<string | null>(null)

  const { enqueue, snapshot, discard } = useWriteQueue()

  // The read half of SAL-011. Not queued: OFF-001a is about writes, and CustomerSearch already
  // establishes the pattern for a screen that has to reach the API directly to read.
  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    void doFetch(`${baseUrl}/orders/returns/${encodeURIComponent(orderId)}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new PolicyRefused(r.status)
        return (await r.json()) as ReturnsPolicyResponse
      })
      .then((body) => {
        if (cancelled) return
        setLines(body.lines ?? [])
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Refused, not degraded: SAL-011's notice was never shown, so no line below is offered.
        setLines(null)
        setLoadError(
          error instanceof PolicyRefused
            ? { kind: 'refused', status: error.status }
            : { kind: 'other', message: error instanceof Error ? error.message : null },
        )
      })
    return () => {
      cancelled = true
    }
  }, [baseUrl, doFetch, orderId, token, reloadToken])

  const selectedLine = useMemo(
    () => lines?.find((l) => l.orderLineId === selectedLineId) ?? null,
    [lines, selectedLineId],
  )
  const remaining = selectedLine ? selectedLine.quantitySold - selectedLine.quantityReturned : 0

  const currentWrite = writeId ? snapshot.writes.find((w) => w.id === writeId) : undefined

  const selectLine = useCallback((lineId: string) => {
    setSelectedLineId(lineId)
    setQuantity(1)
    setReason('')
    setRestocked(false)
    setWriteId(null)
  }, [])

  // ARC-006 at the screen, the same concern RxCapture names in its own comments: "the button is
  // reachable twice in the time it takes a slow tablet to respond, and a shop will press it twice."
  // `submitDisabled` reacting to `currentWrite` protects every click after the first render, but two
  // taps close enough together can both fire before that render happens, since React has not
  // committed anything yet at the moment the second one starts. A ref is synchronous and does not
  // wait for a render: it is set true before the first `await` in this function runs, so a second
  // call arriving in the same tick sees it immediately and returns rather than discarding and
  // re-enqueueing a second time.
  const submitting = useRef(false)

  const submit = useCallback(
    async (goodwillOverride: boolean) => {
      if (!selectedLine || submitting.current) return
      submitting.current = true
      try {
        // A blocked write for this line is discarded first, always, before a new one is queued. The
        // queue drains strictly in order, so a still-blocked write ahead of a new one would hold the
        // new one behind it forever: that is what a blocked write is for (a payment must not
        // overtake the sale it belongs to), and it applies just as much to a corrected retry of a
        // return as to anything else. Sequenced, not fired together, so the new write never lands
        // behind a still-draining queue.
        if (currentWrite?.status === 'blocked') await discard(currentWrite.id)

        // enqueue's return type is deliberately `unknown` at the hook boundary (OFF-001a: a
        // component has nothing to call with, and that includes not knowing the transport's
        // response shape). RxCapture casts the same way, to the one field this screen actually
        // reads.
        //
        // This is always a fresh write with a fresh idempotency key, never a retry carrying the old
        // one. ARC-006 is about not counting one request twice, and a corrected reason or an added
        // goodwillOverride is a materially different request, deliberately a second one rather than
        // the same one sent again.
        const write = (await enqueue({
          method: 'POST',
          path: '/orders/returns',
          // No item description reaches this screen from GET /orders/returns/:orderId (it carries
          // no product name), so the label is built from what is actually known rather than
          // invented.
          label: t('returnLine.queue.returnLabel', { quantity, orderId }),
          body: {
            orderId,
            orderLineId: selectedLine.orderLineId,
            branchId,
            terminalId,
            ...(cashSessionId === undefined ? {} : { cashSessionId }),
            quantity,
            reason: reason.trim(),
            ...(restocked ? { restocked: true } : {}),
            ...(goodwillOverride ? { goodwillOverride: true } : {}),
          },
        })) as { id: string }
        setWriteId(write.id)
      } finally {
        submitting.current = false
      }
    },
    [
      branchId,
      cashSessionId,
      currentWrite,
      discard,
      enqueue,
      orderId,
      quantity,
      reason,
      restocked,
      selectedLine,
      t,
      terminalId,
    ],
  )

  const confirmGoodwillOverride = useCallback(() => {
    if (!currentWrite) return
    const reasonText = currentWrite.error?.message ?? t('returnLine.overrideFallback')
    // A deliberate, separate action, per the task: never a checkbox that is easy to leave ticked.
    // Names the refusal verbatim (DESIGN-TOKENS.md's destructive-confirm rule: name the exact
    // consequence) and asks again, the same shape BlockedWrites already uses before a discard.
    const ok = window.confirm(t('returnLine.goodwillConfirm', { reason: reasonText }))
    if (!ok) return
    void submit(true)
  }, [currentWrite, submit, t])

  useEffect(() => {
    // A return went through: the shop's counts are stale until the policy is re-read, so the
    // remaining quantity shown is the real one rather than one this screen would have to guess at.
    //
    // Deliberately keyed on the status string alone, not on `currentWrite` or `onDone`. Both are a
    // new reference on every snapshot tick and every parent render; depending on them would refire
    // this effect, and therefore the reload fetch, on every tick for as long as the write stays
    // `sent`, rather than once when it first becomes `sent`.
    if (currentWrite?.status === 'sent') {
      setReloadToken((n) => n + 1)
      onDone?.()
    }
  }, [currentWrite?.status])

  // A blocked write for an override-eligible code (RETURN_MADE_TO_SPECIFICATION and the like) has
  // exactly one deliberate way forward, the goodwill button below, so the plain Return button stays
  // disabled rather than offering a second, ordinary-looking way to send the same refusal again.
  const blockedNeedsOverride =
    currentWrite?.status === 'blocked' &&
    currentWrite.error?.code !== undefined &&
    OVERRIDE_ELIGIBLE_CODES.has(currentWrite.error.code) &&
    !writeIsGoodwillOverride(currentWrite)

  // Otherwise, Return is disabled only while a write is genuinely in flight or already sent. A
  // blocked write that is not override-eligible (RETURN_REASON_REQUIRED, a bad quantity, a
  // permission the staff member turns out not to have) is a correctable mistake, and the
  // shopkeeper fixes the field and presses Return again rather than being made to reselect the line
  // to get a working button back.
  const submitDisabled =
    !selectedLine ||
    quantity < 1 ||
    quantity > remaining ||
    reason.trim().length < 3 ||
    (currentWrite !== undefined && currentWrite.status !== 'blocked') ||
    blockedNeedsOverride

  return (
    <section className="lm-screen" aria-labelledby="return-line-heading">
      <h1 id="return-line-heading" className="lm-screen__heading">
        {t('app.returnLine')}
      </h1>

      {/*
        Only the list is inside the surface: the return form below is driven by the selected line and
        the queued write, not by this read. A failed read is an error here and never an empty list,
        because SAL-011's notice was not shown and no line may be offered without it. Loading is
        decided by `lines === null` and not the `loading` flag, since a reload after a sent return
        keeps the lines and would otherwise flash the whole list to a skeleton.
      */}
      <DataSurface
        state={
          loadError !== null
            ? {
                kind: 'error',
                message:
                  loadError.kind === 'refused'
                    ? t('returnLine.policyRefused', { status: loadError.status })
                    : (loadError.message ?? t('returnLine.policyUnreachable')),
                onRetry: () => setReloadToken((n) => n + 1),
              }
            : lines === null
              ? { kind: 'loading' }
              : lines.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={2}
        what={t('returnLine.what')}
        emptyMessage={t('common.orderNoLines')}
      >
        <ul className="lm-matches" aria-label={t('returnLine.linesAriaLabel')}>
          {(lines ?? []).map((line) => {
            const lineRemaining = line.quantitySold - line.quantityReturned
            return (
              <li key={line.orderLineId}>
                <button
                  type="button"
                  className={
                    line.orderLineId === selectedLineId
                      ? 'lm-match lm-match--highlighted'
                      : 'lm-match'
                  }
                  aria-pressed={line.orderLineId === selectedLineId}
                  disabled={lineRemaining <= 0}
                  onClick={() => selectLine(line.orderLineId)}
                >
                  <span className="lm-match__name">
                    {/* Two counts in one sentence: "2 of 3 left" reads as "3 of 2 left" if the
                        digits take the paragraph's direction. */}
                    {sentence(t, 'returnLine.linesLeft', {
                      remaining: <Ltr>{lineRemaining}</Ltr>,
                      sold: <Ltr>{line.quantitySold}</Ltr>,
                    })}
                  </span>
                  <span className="lm-match__why">
                    {returnsPositionLabel(t, line.returnsPosition)}
                  </span>
                </button>
                {/* Shown per line, verbatim, so the notice a shopkeeper reads is the one for the
                    line they are about to pick, not a generic sentence composed on this screen.
                    Static content read in sequence with the row, not a live region: there is
                    nothing here that changes after the page settles. */}
                <div className="lm-paste-report">
                  <p className="lm-paste-report__line">{line.notice}</p>
                </div>
              </li>
            )
          })}
        </ul>
      </DataSurface>

      {selectedLine ? (
        <form
          className="lm-form"
          onSubmit={(e) => {
            e.preventDefault()
            void submit(false)
          }}
        >
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('common.quantity')}</span>
            <input
              className="lm-input"
              type="number"
              min={1}
              max={remaining}
              step={1}
              value={quantity}
              onChange={(e) => {
                const next = Number(e.currentTarget.value)
                if (!Number.isNaN(next)) setQuantity(next)
              }}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('common.reason')}</span>
            <textarea
              className="lm-input"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field lm-field--inline">
            <input
              type="checkbox"
              checked={restocked}
              onChange={(e) => setRestocked(e.currentTarget.checked)}
            />
            <span className="lm-field__label">{t('returnLine.restockedLabel')}</span>
          </label>

          <div className="lm-screen__actions">
            <button
              type="submit"
              className="lm-button lm-button--primary"
              disabled={submitDisabled}
            >
              {t('returnLine.returnButton')}
            </button>
          </div>
        </form>
      ) : null}

      {currentWrite ? (
        <ReturnWriteStatus
          write={currentWrite}
          onConfirmGoodwillOverride={confirmGoodwillOverride}
        />
      ) : null}
    </section>
  )
}

/**
 * What the queued return write is doing, in one place: sent, still on the device, refused outright,
 * or refused in a way `goodwill_override` can still get through. Mirrors `RxCapture`'s `statusLine`:
 * one sentence about one write, never a claim the server has not actually made.
 */
/**
 * The refund off a sent return's own response, or `undefined` when the answer is not the shape this
 * screen knows. Never thrown: the return itself succeeded, and a shape this screen did not
 * anticipate is a reason to say less rather than to replace a confirmation with a crash.
 */
function refundOf(
  response: unknown,
): { readonly minor: string; readonly currency: string } | undefined {
  if (typeof response !== 'object' || response === null) return undefined
  const refund = (response as { refund?: unknown }).refund
  if (typeof refund !== 'object' || refund === null) return undefined
  const { minor, currency } = refund as { minor?: unknown; currency?: unknown }
  if (typeof minor !== 'string' || typeof currency !== 'string') return undefined
  return { minor, currency }
}

function ReturnWriteStatus({
  write,
  onConfirmGoodwillOverride,
}: {
  readonly write: QueuedWrite
  readonly onConfirmGoodwillOverride: () => void
}): JSX.Element {
  const { t } = useTranslation()
  if (write.status === 'sent') {
    /**
     * **The amount, not just the word.**
     *
     * `returns.service.ts` caps the cash handed back at what was actually paid in cash, so on an
     * udhaar order the refund is less than the line's gross and the receipt price is the wrong
     * number to count out of the drawer. This screen held no other copy of the real figure and no
     * other screen shows it, so "Returned." was the whole of what the person at the till got.
     *
     * Read off the write's own response, which `queue.ts` stores. This file's docstring used to say
     * the transport carried no success body; it does.
     */
    const refund = refundOf(write.response)
    const refundMinor = refund === undefined ? undefined : parseMinorUnits(refund.minor)
    return (
      <p className="lm-queue-state" role="status">
        {refund === undefined || refundMinor === undefined
          ? t('returnLine.returned')
          : refundMinor === 0n
            ? /*
               * **Zero is a real answer here, and "PKR 0.00 goes back" is not the way to give it.**
               *
               * The server's `refund` is the **cash** half, capped at what was actually paid in
               * cash. On an udhaar order where nothing was paid at the counter, that is zero: no
               * notes leave the drawer, and what the customer gets is their balance reduced by the
               * refund journal. Printing a zero amount beside the words "goes back" reads as a
               * failed refund, and somebody would count nothing out and then go looking for the
               * fault.
               */
              t('returnLine.returnedNoCash')
            : sentence(t, 'returnLine.returnedRefund', {
                amount: <Ltr>{formatMoneyForDisplay(refundMinor, refund.currency)}</Ltr>,
              })}
      </p>
    )
  }

  if (write.status !== 'blocked') {
    return (
      <p className="lm-queue-state" role="status">
        {t('returnLine.queuedOnDevice')}
      </p>
    )
  }

  const code = write.error?.code
  const message = write.error?.message ?? t('returnLine.noReason')
  const alreadyOverride = writeIsGoodwillOverride(write)

  if (code !== undefined && OVERRIDE_ELIGIBLE_CODES.has(code) && !alreadyOverride) {
    return (
      <div className="lm-blocked" aria-labelledby="return-line-override-heading">
        <h2 id="return-line-override-heading" className="lm-blocked__heading">
          {t('returnLine.overrideHeading')}
        </h2>
        {/* The server's own words, unchanged: it is careful not to state a policy the shop has not
            configured, and a sentence composed here could say something it did not. */}
        <p className="lm-blocked__reason">{message}</p>
        <div className="lm-blocked__actions">
          <button
            type="button"
            className="lm-button lm-button--danger"
            onClick={onConfirmGoodwillOverride}
          >
            {t('returnLine.goodwillButton')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <p className="lm-queue-state" role="status">
      {code
        ? t('returnLine.notReturnedCode', { message, code })
        : t('returnLine.notReturned', { message })}
    </p>
  )
}
